import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
const videos = [{
  url: 'https://cdn.magicpatterns.com/uploads/cs5TdcgXgtae3rJcUL9R4s/IA inmo-2.mp4',
  type: 'Proyecto 3D'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/hFaej3v7VxNFXX5SVL2RZ2/IA inmo-1.mp4',
  type: 'Casa Terminada'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/agCNvjFFNZTfcFiBDww59b/IA inmo.mp4',
  type: 'Recorrido Virtual'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/xu9Htcvd7MQbnx4nEgJX6q/IA inmo-6.mp4',
  type: 'Interiorismo'
}, {
  url: 'https://cdn.magicpatterns.com/uploads/6c7ZtBvPNBkKYeUtrkMbRV/IA inmo-5.mp4',
  type: 'Área Social'
}];
export function Features() {
  const trackRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const posRef = useRef(0); // float accumulator (scrollLeft truncates to int)
  const resumeTimer = useRef<number | null>(null);
  const videosRef = useRef<HTMLVideoElement[]>([]);
  // Duplicate the list so the scroll can loop seamlessly.
  const loopVideos = [...videos, ...videos];
  // Mobile browsers (esp. iOS Safari) are strict about muted autoplay: they
  // need playsInline + an actually-muted element, often only play in-viewport
  // videos, and sometimes require a first user gesture. Cover all bases.
  useEffect(() => {
    const tryPlayAll = () => {
      videosRef.current.forEach(v => {
        if (!v) return;
        v.muted = true;
        const p = v.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      });
    };
    // 1) Attempt right away.
    tryPlayAll();
    // 2) Play each video as it scrolls into view.
    let observer: IntersectionObserver | null = null;
    if ('IntersectionObserver' in window) {
      observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          const v = entry.target as HTMLVideoElement;
          if (entry.isIntersecting) {
            v.muted = true;
            const p = v.play();
            if (p && typeof p.catch === 'function') p.catch(() => {});
          }
        });
      }, {
        threshold: 0.1
      });
      videosRef.current.forEach(v => v && observer?.observe(v));
    }
    // 3) Unlock playback on the first user gesture (some mobile cases).
    const onGesture = () => {
      tryPlayAll();
      window.removeEventListener('touchstart', onGesture);
      window.removeEventListener('click', onGesture);
    };
    window.addEventListener('touchstart', onGesture, {
      passive: true
    });
    window.addEventListener('click', onGesture);
    return () => {
      observer?.disconnect();
      window.removeEventListener('touchstart', onGesture);
      window.removeEventListener('click', onGesture);
    };
  }, []);
  useEffect(() => {
    const el = trackRef.current;
    if (!el) return;
    let frame: number;
    const SPEED = 0.45; // px per frame ≈ slow, premium drift
    const tick = () => {
      if (el) {
        const half = el.scrollWidth / 2;
        if (pausedRef.current) {
          // While the user is in control, mirror their scroll position so the
          // auto-drift resumes seamlessly from wherever they left it.
          posRef.current = el.scrollLeft;
        } else if (half > 0) {
          posRef.current += SPEED;
          if (posRef.current >= half) posRef.current -= half;
          // Keep the precise float in posRef even though the DOM truncates
          // scrollLeft to an integer — otherwise sub-pixel steps never move.
          el.scrollLeft = posRef.current;
        }
      }
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, []);
  const pause = () => {
    pausedRef.current = true;
    if (resumeTimer.current) {
      clearTimeout(resumeTimer.current);
      resumeTimer.current = null;
    }
  };
  // Resume auto-scroll a moment after the user stops interacting.
  const scheduleResume = (delay = 1200) => {
    if (resumeTimer.current) clearTimeout(resumeTimer.current);
    resumeTimer.current = window.setTimeout(() => {
      const el = trackRef.current;
      if (el) posRef.current = el.scrollLeft;
      pausedRef.current = false;
    }, delay);
  };
  // Keep the loop seamless if the user drags past either edge.
  const handleScroll = () => {
    const el = trackRef.current;
    if (!el || !pausedRef.current) return;
    const half = el.scrollWidth / 2;
    if (el.scrollLeft >= half) {
      el.scrollLeft -= half;
    } else if (el.scrollLeft <= 0) {
      el.scrollLeft += half;
    }
  };
  return <section className="py-[64px] md:py-[96px] relative z-10 overflow-hidden bg-bg-secondary" data-id="element-100">
      <div className="container mx-auto px-5 max-w-[1100px]" data-id="element-101">
        <motion.div initial={{
        opacity: 0,
        y: 24
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }} className="text-center mb-12" data-id="element-102">
          <h2 className="text-[32px] md:text-[48px] font-[900] mb-4 uppercase text-white" data-id="element-103">
            MIRA LO QUE PODRÁS CREAR
          </h2>
          <p className="text-text-secondary text-[18px] font-[500]" data-id="element-104">
            Todos estos videos fueron generados con IA a partir de simples fotos
            de propiedades.
          </p>
        </motion.div>
      </div>

      {/* Infinite marquee — auto-drifts left and is draggable / scrollable */}
      <div className="relative w-full overflow-hidden" data-id="element-105">
        {/* Edge fade masks */}
        <div className="pointer-events-none absolute left-0 top-0 bottom-0 w-16 md:w-32 z-10 bg-gradient-to-r from-bg-secondary to-transparent" data-id="element-106" />
        <div className="pointer-events-none absolute right-0 top-0 bottom-0 w-16 md:w-32 z-10 bg-gradient-to-l from-bg-secondary to-transparent" data-id="element-107" />

        <div ref={trackRef} onScroll={handleScroll} onPointerEnter={pause} onPointerLeave={() => scheduleResume(800)} onPointerDown={pause} onPointerUp={() => scheduleResume(1800)} onTouchStart={pause} onTouchEnd={() => scheduleResume(1800)} onWheel={() => {
        pause();
        scheduleResume(1500);
      }} className="flex gap-[24px] px-3 md:px-6 overflow-x-auto scrollbar-hide cursor-grab active:cursor-grabbing select-none" style={{
        scrollbarWidth: 'none'
      }} data-id="element-108">
          {loopVideos.map((video, idx) => <div key={idx} aria-hidden={idx >= videos.length} className="min-w-[260px] md:min-w-[300px] bg-bg-card border border-border-card rounded-[16px] p-4 group hover:border-accent-border hover:shadow-[0_12px_40px_rgba(0,180,230,0.1)] transition-all duration-250 flex-shrink-0" data-id="element-109">
              <div className="aspect-[9/16] bg-bg-primary rounded-[8px] mb-4 relative overflow-hidden border border-border-subtle" data-id="element-110">
                <video ref={el => {
              if (!el) return;
              // React doesn't reliably set the `muted` DOM property, and
              // browsers block autoplay on non-muted videos. Force it,
              // then kick off playback.
              el.muted = true;
              el.defaultMuted = true;
              if (!videosRef.current.includes(el)) {
                videosRef.current.push(el);
              }
              const p = el.play();
              if (p && typeof p.catch === 'function') p.catch(() => {});
            }} src={encodeURI(video.url)} autoPlay loop muted playsInline preload="auto" className="absolute inset-0 w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-111" />
              </div>
              <div className="flex justify-center" data-id="element-112">
                <span className="inline-block bg-accent-glow text-accent border border-accent-border rounded-[8px] px-3 py-1 text-[12px] font-bold uppercase" data-id="element-113">
                  ✦ {video.type}
                </span>
              </div>
            </div>)}
        </div>
      </div>
    </section>;
}